import Notify from 'notifyjs'
import { observable, runInAction } from 'mobx'
import { localStorage as storage } from '@/infra/storage'
import { type Observable, NEVER } from 'rxjs'
import { type SystemNotification } from './'

import defaultIcon from '@/assets/misc/img_taxfyle-symbol.svg'

/**
 * Provides the ability to show desktop notifications.
 */
export class DesktopNotificationStore {
  @observable accessor isSupported = false
  @observable accessor isEnabled = false
  @observable accessor isDenied = false

  constructor(authenticated$: Observable<unknown>) {
    this.show = this.show.bind(this)
    this.isSupported = !Notify.needsPermission || Notify.isSupported()

    if (this.isSupported) {
      this.isDenied = Notification.permission === 'denied'
    }

    authenticated$.subscribe(() => {
      // Restore enabled state.
      const stored = storage.getItem('desktopNotifications')
      const enabled = stored !== 'disabled'
      void this.setEnabled(enabled, stored !== null).then()
    })
  }

  /**
   * Enables or disables desktop notifications.
   *
   * @param enabled
   * @param noShowWelcome If `true`, does not show the welcome notification
   *  when enabled.
   */
  async setEnabled(enabled: boolean, noShowWelcome = false) {
    if (enabled) {
      if (this.isEnabled && !Notify.needsPermission) {
        return
      }
      enabled = await this._getPermission()
      runInAction(() => {
        this.isDenied = !enabled
      })
    }

    storage.setItem('desktopNotifications', enabled ? 'enabled' : 'disabled')
    runInAction(() => {
      this.isEnabled = enabled
    })

    if (this.isEnabled && !noShowWelcome) {
      this.show({
        title: 'Nice!',
        body: 'Desktop notifications have been enabled. You rock!',
        tag: 'notifications-enabled',
      })
    }
  }

  /**
   * Shows a desktop notification.
   *
   * @param opts
   */
  show(opts: SystemNotification) {
    if (!this.isEnabled) {
      return
    }

    // If an observable is passed to `opts.close$`, close the notification
    // when it emits.
    const closeSubscription = (opts.close$ ?? NEVER).subscribe(() => {
      notification?.close()
    })

    const notification = new Notify(opts.title, {
      tag: opts.tag,
      body: opts.body,
      icon: opts.icon || defaultIcon,
      timeout: opts.timeout ?? 5,
      notifyError: () => {
        closeSubscription?.unsubscribe()
      },
      notifyClick: () => {
        window.focus()
        opts.onClick?.()
        notification.close()
      },
      notifyClose: () => {
        closeSubscription?.unsubscribe()
      },
    })

    notification.show()
  }

  private _getPermission(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!Notify.needsPermission) {
        return resolve(true)
      }

      if (!this.isSupported) {
        return resolve(false)
      }

      Notify.requestPermission(
        () => resolve(true),
        () => resolve(false),
      )
    })
  }
}
